import { useCallback, useEffect, useRef, useState } from 'react';
import { ENDPOINTS } from 'features/ai-assistant/constants';
import { useShowAlert } from 'hooks';
import { ALT_API_CLIENT, getCancelToken } from 'services/API';
import strings from 'strings';
import { UIStatus } from 'UI/constants/status';

const MIN_REQUIRED_QUERY_LENGTH = 3;
const normalizeQuery = value => value.trim().toLowerCase();

export const useSearchModal = () => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const handleOpenSearch = () => setIsSearchOpen(true);
  const handleCloseSearch = () => setIsSearchOpen(false);

  return { isSearchOpen, handleOpenSearch, handleCloseSearch };
};

export const useSearchConversation = ({ query, debounceMs = 400 }) => {
  const { showAlert } = useShowAlert();
  const cancelToken = useRef(null);

  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [results, setResults] = useState([]);
  const [uiStatus, setUiStatus] = useState(UIStatus.Default);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedQuery(normalizeQuery(query));
    }, debounceMs);

    return () => {
      clearTimeout(timeoutId);
      if (cancelToken.current) {
        cancelToken.current.cancel(strings.shared.requests.cancelTokenMessage);
      }
    };
  }, [query, debounceMs]);

  const getResults = useCallback(
    async term => {
      if (cancelToken.current) {
        cancelToken.current.cancel(strings.shared.requests.cancelTokenMessage);
      }
      cancelToken.current = getCancelToken();

      try {
        setUiStatus(UIStatus.Loading);
        setResults([]);

        const response = await ALT_API_CLIENT.get(ENDPOINTS.sessionSearch, {
          cancelToken: cancelToken.current.token,
          params: {
            term
          }
        });
        const data = response.data?.results
          ? response.data.results.filter(result => !!result.sessionId)
          : [];

        setResults(data);

        const isEmpty = data.length === 0;
        setUiStatus(isEmpty ? UIStatus.Empty : UIStatus.Success);
      } catch (error) {
        if (error?.message === strings.shared.requests.cancelTokenMessage) return;

        setUiStatus(UIStatus.Error);
        showAlert({
          error,
          title: strings.formatString(strings.ai.generators.assistant.errors.failedAction, {
            action: 'search'
          })
        });
      }
    },
    [showAlert]
  );

  useEffect(() => {
    if (!debouncedQuery || debouncedQuery.length < MIN_REQUIRED_QUERY_LENGTH) {
      setResults([]);
      setUiStatus(UIStatus.Default);
      return;
    }

    getResults(debouncedQuery);
  }, [debouncedQuery, getResults]);

  return {
    results,
    uiStatus
  };
};
